import type { DomainFilterValue, VisibilityRange } from '../types';
import { isVisibilityRange } from './visibility-range';
import { parseDomainFilterParam, domainFilterToParam } from './domain-storage';
import { parseSourceFilterParam, sourceFilterToParam } from './source-storage';

export interface ParsedUrlFilters {
  range?: VisibilityRange;
  from?: string;
  to?: string;
  topicId?: string | null;
  modelId?: string | null;
  domainFilterId?: DomainFilterValue;
  sourceFilterIds?: string[];
  hasAny: boolean;
}

const FILTER_KEYS = ['range', 'from', 'to', 'topic', 'model', 'domain', 'sources'] as const;

export function parseFiltersFromUrl(params: URLSearchParams): ParsedUrlFilters {
  const rangeParam = params.get('range');
  const range =
    rangeParam && isVisibilityRange(rangeParam) ? rangeParam : undefined;
  const from = params.get('from') ?? undefined;
  const to = params.get('to') ?? undefined;
  const topicId = params.get('topic');
  const modelId = params.get('model');
  const domainParam = params.get('domain');
  const domainFilterId = params.has('domain')
    ? parseDomainFilterParam(domainParam)
    : undefined;
  const sourceFilterIds = params.has('sources')
    ? parseSourceFilterParam(params.get('sources'))
    : undefined;

  const hasAny = FILTER_KEYS.some((key) => params.has(key));

  return {
    range,
    from,
    to,
    topicId: topicId || null,
    modelId: modelId || null,
    domainFilterId,
    sourceFilterIds: sourceFilterIds ?? [],
    hasAny,
  };
}

export function applyFiltersToUrl(
  params: URLSearchParams,
  filters: {
    visibilityRange: VisibilityRange;
    customFrom: string;
    customTo: string;
    topicFilterId: string | null;
    modelFilterId: string | null;
    domainFilterId: DomainFilterValue;
    sourceFilterIds: string[];
    /** When false, leave date params out (auto-resolved default). */
    syncDateRange?: boolean;
  },
): URLSearchParams {
  const next = new URLSearchParams(params);
  const syncDateRange = filters.syncDateRange !== false;

  if (syncDateRange) {
    next.set('range', filters.visibilityRange);
    if (filters.visibilityRange === 'custom' && filters.customFrom && filters.customTo) {
      next.set('from', filters.customFrom);
      next.set('to', filters.customTo);
    } else {
      next.delete('from');
      next.delete('to');
    }
  } else {
    next.delete('range');
    next.delete('from');
    next.delete('to');
  }

  if (filters.topicFilterId) next.set('topic', filters.topicFilterId);
  else next.delete('topic');

  if (filters.modelFilterId) next.set('model', filters.modelFilterId);
  else next.delete('model');

  const domainParam = domainFilterToParam(filters.domainFilterId);
  if (domainParam) next.set('domain', domainParam);
  else next.delete('domain');

  const sourcesParam = sourceFilterToParam(filters.sourceFilterIds);
  if (sourcesParam) next.set('sources', sourcesParam);
  else next.delete('sources');

  return next;
}

export function filtersUrlEquals(a: URLSearchParams, b: URLSearchParams): boolean {
  for (const key of FILTER_KEYS) {
    if ((a.get(key) ?? '') !== (b.get(key) ?? '')) return false;
  }
  return true;
}
